/**
 * Generic field components for the onboarding flow. Schema-driven so step
 * components stay tiny.
 *
 * Every field reports changes via `onChange(value)`. The step layer is the
 * one that turns a change into a debounced auto-save patch.
 *
 * Inline validation (Bug 3 fix, Feb 2026): TextInput accepts an optional
 * `validate` function that runs on blur and renders an inline error message
 * underneath the field. Errors are advisory ONLY — the auto-save still
 * fires, and the server does its own validation. We never block typing.
 */
import { useState } from "react";
import { Plus, Trash2, Check, ChevronDown, Search } from "lucide-react";
import {
  Select as ShadSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "../../../components/ui/command";

const inputBase =
  "w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600";

const inputBaseError =
  "w-full rounded-xl bg-white/[0.03] border border-rose-400/40 focus:border-rose-400/60 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-rose-400/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600";

// ----- Built-in validators ------------------------------------------------

const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164-friendly: optional +, then 7-15 digits, allowing spaces/dashes.
const PHONE_RE = /^\+?[\d][\d\s\-]{6,18}\d$/;

export const VALIDATORS = {
  url: (v) => {
    const s = (v || "").trim();
    if (!s) return null;
    if (!/^https:\/\//i.test(s)) {
      // Match the backend rule (url_validator.py) — HTTPS only. Accepting
      // `http://` here would cause the backend to silently drop the value
      // and the merchant would be confused why their step never marks
      // complete.
      return "Must start with https://";
    }
    if (!URL_RE.test(s)) {
      return "That doesn't look like a valid URL.";
    }
    return null;
  },
  email: (v) => {
    const s = (v || "").trim();
    if (!s) return null;
    return EMAIL_RE.test(s) ? null : "Enter a valid email address.";
  },
  phone: (v) => {
    const s = (v || "").trim();
    if (!s) return null;
    if (!s.startsWith("+")) {
      return "Include the country code, e.g. +357 …";
    }
    return PHONE_RE.test(s) ? null : "That phone number doesn't look right.";
  },
  fullName: (v) => {
    const s = (v || "").trim();
    if (!s) return null;
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return "Please enter both first and last name.";
    if (parts.some((p) => p.length < 2)) {
      return "Each name part should be at least 2 characters.";
    }
    return null;
  },
};

export function FieldShell({ label, required, hint, children, testId, error }) {
  return (
    <div data-testid={testId}>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
        {label} {required && <span className="text-[#00E5FF]">*</span>}
      </label>
      {children}
      {error ? (
        <div data-testid={`${testId}-error`} className="mt-1.5 text-[11.5px] text-rose-300">
          {error}
        </div>
      ) : (
        hint && <div className="mt-1.5 text-[11px] text-zinc-500">{hint}</div>
      )}
    </div>
  );
}

export function TextInput({ value, onChange, onBlur, placeholder, testId, type = "text", multiline, rows = 4, validate }) {
  const [error, setError] = useState(null);
  const handleBlur = (e) => {
    if (validate) setError(validate(e.target.value));
    onBlur?.(e);
  };
  const handleChange = (e) => {
    if (error) {
      // Clear error as soon as the user edits — re-validate on next blur.
      setError(null);
    }
    onChange(e.target.value);
  };
  const props = {
    value: value ?? "",
    onChange: handleChange,
    onBlur: handleBlur,
    placeholder,
    "data-testid": testId,
    "aria-invalid": error ? "true" : undefined,
  };
  const cls = error ? inputBaseError : inputBase;
  return (
    <>
      {multiline ? (
        <textarea {...props} rows={rows} className={`${cls} resize-y min-h-[100px]`} />
      ) : (
        <input type={type} {...props} className={cls} />
      )}
      {error && (
        <div data-testid={`${testId}-validation-error`} className="mt-1.5 text-[11.5px] text-rose-300">
          {error}
        </div>
      )}
    </>
  );
}

export function NumberInput({ value, onChange, onBlur, placeholder, testId, step = "any" }) {
  return (
    <input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      onBlur={onBlur}
      placeholder={placeholder}
      data-testid={testId}
      className={inputBase}
    />
  );
}

// Threshold above which we render a type-ahead Combobox instead of the
// plain Select dropdown. Below this, the Select is faster (no extra
// keyboard input row taking visual real-estate).
const SEARCHABLE_OPTIONS_THRESHOLD = 8;

function SearchableSelect({ value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const placeholder = value || "Select…";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`${inputBase} cursor-pointer flex items-center justify-between gap-2 text-left ${
            value ? "text-white" : "text-zinc-600"
          }`}
        >
          <span className="truncate">{placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[60] p-0 w-[--radix-popover-trigger-width] min-w-[260px] rounded-xl border-white/[0.08] bg-[#0b0b0e] text-white shadow-xl shadow-black/40"
      >
        <Command
          className="bg-transparent"
          // cmdk's default filter is case-insensitive substring match —
          // perfect for country / currency / role lookups.
        >
          <div className="flex items-center gap-2 border-b border-white/[0.06] px-3">
            <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <CommandInput
              placeholder="Type to filter…"
              data-testid={testId ? `${testId}-search` : undefined}
              className="h-10 bg-transparent text-[14px] text-white placeholder:text-zinc-600 outline-none border-0 focus:ring-0"
            />
          </div>
          <CommandList className="max-h-[280px] overflow-y-auto py-1">
            <CommandEmpty className="px-3 py-6 text-center text-[13px] text-zinc-500">
              No match
            </CommandEmpty>
            {options.map((o) => (
              <CommandItem
                key={o}
                value={o}
                data-testid={testId ? `${testId}-option-${o}` : undefined}
                onSelect={() => { onChange(o); setOpen(false); }}
                className="flex items-center justify-between gap-2 px-3 py-2 text-[14px] text-zinc-200 aria-selected:bg-white/[0.06] aria-selected:text-white cursor-pointer rounded-md mx-1"
              >
                <span className="truncate">{o}</span>
                {value === o && (
                  <Check className="h-3.5 w-3.5 text-[#00E5FF] shrink-0" />
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function SelectInput({ value, onChange, options, testId }) {
  // For long option lists, render a type-ahead combobox so merchants
  // can jump from "C" → "Colombia" in two keystrokes rather than
  // scrolling through 63 countries. Short lists keep the plain Select
  // which is faster for ≤7 items.
  if ((options?.length || 0) >= SEARCHABLE_OPTIONS_THRESHOLD) {
    return (
      <SearchableSelect
        value={value} onChange={onChange} options={options} testId={testId}
      />
    );
  }
  // Radix-based portal dropdown so the popup respects the dark theme.
  // Native <select> popups render with the browser/OS chrome (white bg
  // in Chrome/Safari) which clashes with the rest of the form.
  return (
    <ShadSelect value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className={`${inputBase} cursor-pointer h-auto data-[placeholder]:text-zinc-600 [&>span]:text-left`}
      >
        <SelectValue placeholder="Select…" />
      </SelectTrigger>
      <SelectContent
        className="z-[60] max-h-[320px] rounded-xl border-white/[0.08] bg-[#0b0b0e] text-white shadow-xl shadow-black/40"
      >
        {options.map((o) => (
          <SelectItem
            key={o}
            value={o}
            data-testid={testId ? `${testId}-option-${o}` : undefined}
            className="text-[14px] text-zinc-200 focus:bg-white/[0.06] focus:text-white data-[state=checked]:text-[#00E5FF] cursor-pointer"
          >
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadSelect>
  );
}

export function CheckboxRow({ checked, onChange, label, testId }) {
  return (
    <label data-testid={testId} className="flex items-start gap-3 cursor-pointer group">
      <span
        className={`mt-0.5 h-4 w-4 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
          checked
            ? "bg-[#00E5FF] border-[#00E5FF]"
            : "bg-transparent border-white/[0.18] group-hover:border-white/[0.35]"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="h-3 w-3 text-black" fill="none">
            <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <span className="text-[13.5px] text-zinc-200 leading-relaxed">{label}</span>
    </label>
  );
}

/** Tag-style multi-string input. Press Enter or comma to add. */
export function TagsInput({ value, onChange, placeholder, testId }) {
  const list = Array.isArray(value) ? value : [];
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const v = e.currentTarget.value.trim();
      if (v && !list.includes(v)) onChange([...list, v]);
      e.currentTarget.value = "";
    }
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testId} className="rounded-xl bg-white/[0.03] border border-white/[0.08] focus-within:border-[#00E5FF]/50 px-2 py-2 flex flex-wrap gap-1.5">
      {list.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] px-2.5 py-1 text-[12px] text-white">
          {t}
          <button type="button" onClick={() => remove(i)} className="text-zinc-400 hover:text-white">
            <Trash2 className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        onKeyDown={handleKey}
        placeholder={list.length ? "" : placeholder}
        className="flex-1 min-w-[120px] bg-transparent text-[13px] text-white placeholder:text-zinc-600 px-2 py-1 outline-none"
      />
    </div>
  );
}

/** Multi-select rendered as a checkbox grid. Used for array_of_string
 *  fields with a fixed `item_enum` (sales channels, currencies, payment
 *  methods). Always emits the array in the user's selection order. */
export function CheckboxGroup({ value, onChange, options, testId }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 rounded-xl bg-white/[0.02] border border-white/[0.08] p-2"
    >
      {options.map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <label
            key={opt}
            data-testid={`${testId}-opt-${opt}`}
            className={`group flex items-center gap-2.5 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
              isOn ? "bg-[#00E5FF]/[0.08] border border-[#00E5FF]/30" : "border border-transparent hover:bg-white/[0.04]"
            }`}
          >
            <span
              className={`h-4 w-4 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                isOn
                  ? "bg-[#00E5FF] border-[#00E5FF]"
                  : "bg-transparent border-white/[0.18] group-hover:border-white/[0.35]"
              }`}
            >
              {isOn && (
                <svg viewBox="0 0 16 16" className="h-3 w-3 text-black" fill="none">
                  <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <input
              type="checkbox"
              checked={isOn}
              onChange={() => toggle(opt)}
              className="sr-only"
            />
            <span className="text-[13px] text-white">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

/** Repeating people sub-form (directors, UBOs). */
export function PeopleList({ value, onChange, itemFields, addLabel, testId }) {
  const list = Array.isArray(value) ? value : [];
  const setItem = (idx, patch) => onChange(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const add = () => onChange([...list, {}]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div data-testid={testId} className="space-y-3">
      {list.map((item, idx) => (
        <div key={idx} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">— Person {idx + 1}</div>
            <button
              type="button"
              onClick={() => remove(idx)}
              data-testid={`${testId}-remove-${idx}`}
              className="text-[11.5px] text-zinc-500 hover:text-rose-300 transition-colors flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(itemFields).map(([k, spec]) => (
              <FieldShell key={k} label={spec.label} required={spec.required} testId={`${testId}-${idx}-${k}`}>
                {spec.type === "date" ? (
                  <TextInput type="date" value={item[k]} onChange={(v) => setItem(idx, { [k]: v })} testId={`${testId}-${idx}-${k}-input`} />
                ) : spec.type === "number" ? (
                  <NumberInput value={item[k]} onChange={(v) => setItem(idx, { [k]: v })} testId={`${testId}-${idx}-${k}-input`} />
                ) : spec.enum ? (
                  <SelectInput
                    value={item[k]}
                    onChange={(v) => setItem(idx, { [k]: v })}
                    options={spec.enum}
                    testId={`${testId}-${idx}-${k}-input`}
                  />
                ) : k === "full_name" ? (
                  <TextInput
                    value={item[k]}
                    onChange={(v) => setItem(idx, { [k]: v })}
                    testId={`${testId}-${idx}-${k}-input`}
                    validate={VALIDATORS.fullName}
                  />
                ) : (
                  <TextInput value={item[k]} onChange={(v) => setItem(idx, { [k]: v })} testId={`${testId}-${idx}-${k}-input`} />
                )}
              </FieldShell>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid={`${testId}-add`}
        className="w-full rounded-2xl border border-dashed border-white/[0.10] hover:border-[#00E5FF]/40 hover:bg-[#00E5FF]/[0.04] transition-colors py-4 text-[13px] text-zinc-400 hover:text-white inline-flex items-center justify-center gap-1.5"
      >
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </button>
    </div>
  );
}

/** {country, pct} repeating list with a sum-to-100 hint. */
export function CountrySplit({ value, onChange, testId, countryOptions }) {
  const list = Array.isArray(value) ? value : [];
  const total = list.reduce((s, it) => s + (Number(it?.pct) || 0), 0);
  const setItem = (idx, patch) => onChange(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const add = () => onChange([...list, { country: "", pct: 0 }]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div data-testid={testId} className="space-y-2">
      {list.map((item, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_120px_36px] gap-2 items-center">
          {countryOptions ? (
            <SelectInput
              value={item.country}
              onChange={(v) => setItem(idx, { country: v })}
              options={countryOptions}
              testId={`${testId}-${idx}-country`}
            />
          ) : (
            <TextInput
              value={item.country}
              onChange={(v) => setItem(idx, { country: v })}
              placeholder="Country (e.g. France)"
              testId={`${testId}-${idx}-country`}
            />
          )}
          <NumberInput
            value={item.pct}
            onChange={(v) => setItem(idx, { pct: v })}
            placeholder="%"
            testId={`${testId}-${idx}-pct`}
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={`${testId}-remove-${idx}`}
            className="h-10 rounded-xl border border-white/[0.08] text-zinc-500 hover:text-rose-300 hover:bg-white/[0.04] flex items-center justify-center transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={add}
          data-testid={`${testId}-add`}
          className="text-[12px] text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" /> Add country
        </button>
        <div
          className={`text-[11px] uppercase tracking-[0.18em] ${
            total === 100 ? "text-emerald-300" : total > 100 ? "text-rose-300" : "text-zinc-500"
          }`}
        >
          Total: {total}%{total !== 100 && " · must equal 100%"}
        </div>
      </div>
    </div>
  );
}
